import React, { useEffect, useState } from "react";
import { formatPrice } from "../lib/format";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";
import { useSeo, breadcrumb, SITE_URL } from "../lib/seo";

/** Product + Offer + BreadcrumbList for the product page.
 *
 *  Availability is derived per size from `stock_by_size`, so a partially
 *  out-of-stock item is described honestly rather than blanket "InStock".
 *  Returns metadata only (no JSON-LD) while the product is still loading. */
function productSeo(product, cats, id) {
  const path = `/produit/${id}`;
  // No title override while loading: the canonical is already correct, and a
  // "Chargement…" title would be what a slow render or a prerender captures.
  if (!product) return { path };

  const plain = (product.description || "").replace(/^[\s-]+/gm, "").replace(/\s+/g, " ").trim();
  const description = plain
    ? `${plain.slice(0, 155)}${plain.length > 155 ? "…" : ""}`
    : `${product.name} — prêt-à-porter féminin BERULIA. Livraison offerte dès 79 €.`;

  const cat = cats.find(c => c.slug === product.category_slug);
  const parent = cat?.parent_slug ? cats.find(c => c.slug === cat.parent_slug) : null;
  const trail = [{ name: "Accueil", path: "/" }, { name: "Boutique", path: "/boutique" }];
  if (parent) trail.push({ name: (parent.name || "").trim(), path: `/categorie/${parent.slug}` });
  if (cat) trail.push({ name: (cat.name || "").trim(), path: `/categorie/${cat.slug}` });
  trail.push({ name: product.name, path });

  const stock = product.stock_by_size || {};
  const sizes = Object.keys(stock).length ? Object.keys(stock) : (product.sizes || []);
  const offers = sizes.map(s => ({
    "@type": "Offer",
    url: `${SITE_URL}${path}`,
    price: product.price,
    priceCurrency: "EUR",
    availability: (stock[s] ?? 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: { "@id": `${SITE_URL}/#organization` },
    ...(sizes.length > 1 ? { name: `${product.name} — Taille ${s}` } : {}),
  }));

  return {
    title: product.name,
    description,
    path,
    type: "product",
    image: product.images?.[0],
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Product",
          "@id": `${SITE_URL}${path}#product`,
          name: product.name,
          description: plain || product.name,
          url: `${SITE_URL}${path}`,
          image: product.images || [],
          sku: product.id,
          brand: { "@type": "Brand", name: "BERULIA" },
          ...(product.composition ? { material: product.composition.replace(/^[\s-]+/gm, "").replace(/\s+/g, " ").trim() } : {}),
          ...(product.colors?.length ? { color: product.colors.join(", ") } : {}),
          ...(offers.length ? { offers: offers.length === 1 ? offers[0] : offers } : {}),
        },
        breadcrumb(trail),
      ],
    },
  };
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [cats, setCats] = useState([]);
  const [size, setSize] = useState("");
  const [imgIdx, setImgIdx] = useState(0);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then(r => { setProduct(r.data); setSize(""); setImgIdx(0); });
    api.get(`/products/${id}/related`).then(r => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [id]);

  // Categories feed the breadcrumb (parent → child → product).
  useEffect(() => { api.get("/categories").then(r => setCats(r.data)).catch(() => setCats([])); }, []);

  useSeo(productSeo(product, cats, id));

  if (!product) return <div className="py-32 text-center text-berulia-inkmuted">Chargement…</div>;

  const stock = (product.stock_by_size || {})[size] ?? 0;

  const addToCart = () => {
    if (!size) return toast.error("Choisissez une taille");
    if (stock <= 0) return toast.error("Taille épuisée");
    add(product, size, 1);
    toast.success("Ajouté au panier ✿");
  };

  return (
    <>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-2 gap-12">
      <div>
        <div className="aspect-[3/4] bg-berulia-beigewarm overflow-hidden">
          <img src={product.images[imgIdx]} alt={product.name} className="w-full h-full object-cover"/>
        </div>
        {product.images.length > 1 && (
          <div className="grid grid-cols-5 gap-2 mt-3">
            {product.images.map((src,i) => (
              <button key={i} onClick={()=>setImgIdx(i)} className={`aspect-square overflow-hidden border ${i===imgIdx?"border-berulia-ink":"border-berulia-border"}`}>
                <img src={src} className="w-full h-full object-cover" alt=""/>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="lg:pl-8">
        <h1 className="font-heading text-5xl">{product.name}</h1>
        <p className="font-heading text-2xl mt-4">{formatPrice(product.price)}</p>
        <p className="mt-6 text-berulia-inkmuted leading-relaxed whitespace-pre-line">{product.description}</p>

        {product.composition && (
          <div className="mt-6">
            <p className="overline mb-2">Composition</p>
            <p className="text-sm text-berulia-inkmuted leading-relaxed whitespace-pre-line">{product.composition}</p>
          </div>
        )}

        {product.colors && product.colors.length > 0 && (
          <div className="mt-8">
            <p className="overline mb-2">Coloris</p>
            <p className="text-sm text-berulia-inkmuted">{product.colors.join(" · ")}</p>
          </div>
        )}

        <div className="mt-10">
          <div className="mb-3">
            <p className="overline">Taille</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {product.sizes.map(s => {
              const available = (product.stock_by_size || {})[s] > 0;
              return (
                <button key={s} onClick={()=>setSize(s)} data-testid={`size-${s}`}
                  disabled={!available}
                  className={`min-w-[48px] px-3 py-2 overline border transition
                  ${size===s ? "bg-berulia-rosedeep text-berulia-inkdeep border-berulia-ink" : ""}
                  ${available ? "border-berulia-rosedeep/60 hover:bg-berulia-rosedeep hover:text-berulia-inkdeep" : "opacity-30 line-through cursor-not-allowed border-berulia-border"}`}>{s}</button>
              );
            })}
          </div>
          {/* Scarcity nudge: once a size is selected and ≤2 units remain,
              surface a low-stock note. We deliberately don't block adding
              the item to cart — that path stays open since orders are
              decremented atomically at payment time. */}
          {size && stock > 0 && stock <= 2 && (
            <p
              data-testid="low-stock-note"
              className="mt-3 text-sm text-berulia-rosedeep"
            >
              ✿ Plus que {stock === 1 ? "1 exemplaire" : `${stock} exemplaires`} en taille {size}
            </p>
          )}
        </div>

        <button onClick={addToCart} data-testid="add-to-cart-btn"
          className="mt-10 w-full bg-berulia-rosedeep text-berulia-inkdeep overline py-5 hover:bg-berulia-rose transition">Ajouter au panier →</button>

        <div className="mt-10 border-t border-berulia-border pt-6 space-y-2 text-sm text-berulia-inkmuted">
          <p>✿ Livraison offerte dès 79 € en France métropolitaine</p>
          <p>✿ Paiement sécurisé · Retours sous 14 jours (frais à votre charge)</p>
          <p>✿ Une question ? Lia est là, bouton en bas à droite.</p>
        </div>
      </div>
    </div>

    {related.length > 0 && (
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
        <div className="flex items-end justify-between mb-8 border-b border-berulia-border pb-4">
          <h2 className="font-heading text-2xl md:text-3xl text-berulia-inkdeep">À associer</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 md:gap-x-6 gap-y-10">
          {related.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
        </div>
      </section>
    )}
    </>
  );
}
